<template>
  <div class="saved-solutions">
    <div class="container">
      <span class="back-to-playground" @click="backClicked">
        <arrow-svg class="arrow-icon"></arrow-svg>
        Back
      </span>

      <div class="row">
        <div class="col-sm-12 col-md-8">
          <h1 class="page-title">
            My saved solutions
            <tooltip
              :content="
                'Please note, storing your data is offered as a service. <br />We are not responsible for data loss of any kind, like - but not limited to - server crash, tool’s functionality update or addition, phase-out of older products. <br />When intension is to have data stored over a long period of time it is recommended to download a copy to an alternative location that you can access outside of this tool.'
              "
            />
          </h1>
        </div>
        <div class="col-sm-12 col-md-4">
          <div class="search-notes">
            <input
              class="form-control"
              name="search"
              v-model="searchQuery"
              placeholder="Search notes..."
            />
            <cross-svg
              v-if="searchQuery !== null"
              class="cross-icon"
              @click="cleanNotesSearch"
            ></cross-svg>
          </div>
        </div>
      </div>

      <table class="simple-table">
        <thead>
          <th @click="changeSort('region')" :class="sortClass('region')">
            Region
            <up-svg
              class="up-icon"
              v-if="sortClass('region') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="sortClass('region') === 'asc'"
            ></down-svg>
          </th>
          <th @click="changeSort('name')" :class="sortClass('name')">
            Solution Name
            <up-svg
              class="up-icon"
              v-if="sortClass('name') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="sortClass('name') === 'asc'"
            ></down-svg>
          </th>
          <th @click="changeSort('module')" :class="sortClass('module')">
            Module
            <up-svg
              class="up-icon"
              v-if="sortClass('module') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="sortClass('module') === 'asc'"
            ></down-svg>
          </th>
          <th @click="changeSort('driver')" :class="sortClass('driver')">
            Driver
            <up-svg
              class="up-icon"
              v-if="sortClass('driver') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="sortClass('driver') === 'asc'"
            ></down-svg>
          </th>
          <th @click="changeSort('notes')" :class="sortClass('notes')">
            <div v-if="!notesSearch">
              Notes
              <up-svg
                class="up-icon"
                v-if="sortClass('notes') === 'desc'"
              ></up-svg>
              <down-svg
                class="down-icon"
                v-if="sortClass('notes') === 'asc'"
              ></down-svg>
            </div>
          </th>
          <th @click="changeSort('createdOn')" :class="sortClass('createdOn')">
            Date created
            <up-svg
              class="up-icon"
              v-if="sortClass('createdOn') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="sortClass('createdOn') === 'asc'"
            ></down-svg>
          </th>
          <th></th>
        </thead>
        <tbody v-if="sortedSolutions.length">
          <tr
            v-for="(row, index) in sortedSolutions"
            :key="index"
            @mouseover="hover = row.name"
            @mouseleave="hover = ''"
            @click="openSolution(row)"
            :class="{ disabled: row.solution.region !== region }"
          >
            <td>{{ get(row, "solution.module.region") || "-" }}</td>
            <td>
              {{ row.name || "-" }}
            </td>
            <td>{{ get(row, "solution.module.name") || "-" }}</td>
            <td>{{ get(row, "solution.driver.marketingName") || "-" }}</td>
            <td>{{ row.notes || "-" }}</td>
            <td>{{ formatDate(row.createdOn, "yyyy-MM-dd") || "-" }}</td>
            <td style="text-align: right;">
              <transition name="slide-fade" mode="out-in">
                <div>
                  <a
                    href="#"
                    class="link-active link"
                    v-if="hover === row.name"
                    @click.stop="onNoteClicked(row)"
                  >
                    <edit-svg class="edit-icon"></edit-svg>
                  </a>
                  <a
                    href="#"
                    class="link-active link"
                    v-if="hover === row.name"
                    @click.stop="onDeleteClicked(row)"
                  >
                    <delete-svg class="delete-icon"></delete-svg>
                  </a>
                </div>
              </transition>
            </td>
          </tr>
        </tbody>
      </table>

      <p class="meta-info" v-if="!sortedSolutions.length && !loading">
        No solutions have been saved yet.
      </p>
      <p class="meta-info" v-if="loading">Loading...</p>

      <div class="button-container">
        <div v-if="canI('IMPORT_PDF_SELECTION')" class="action-item">
          <label class="btn btn-secondary" for="upload-solution">
            Upload solution
            <input
              v-show="false"
              type="file"
              id="upload-solution"
              ref="solutionFile"
              accept="application/pdf"
              v-on:change="handleSolutionUpload()"
            />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, get } from "lodash";
import { http } from "@/services/http";
import { formatDate } from "@/utils/FormatUtils";
import DeleteSolutionModal from "@/components/modals/DeleteSolutionModal";
import NoteSolutionModal from "@/components/modals/NoteSolutionModal";
import OutOfRegionSolutionModal from "@/components/modals/OutOfRegionSolutionModal";
import deleteSvg from "@/assets/svg/icons/signify-icons_16-delete-16px.svg?inline";
import arrowSvg from "@/assets/svg/icons/signify-icons_13-arrow-button-16px.svg?inline";
import downSvg from "@/assets/svg/icons/signify-icons_04-down-12px.svg?inline";
import upSvg from "@/assets/svg/icons/signify-icons_11-up-12px.svg?inline";
import editSvg from "@/assets/svg/icons/signify-icons_19-edit-16px.svg?inline";
import crossSvg from "@/assets/svg/icons/signify-icons_03-cross-12px.svg?inline";
import { mapState } from "vuex";
import { EventBus } from "@/eventbus.js";

export default {
  name: "SavedSolutions",
  data() {
    return {
      get,
      formatDate,
      solutions: null,
      sorting: null,
      loading: true,
      hover: "",
      searchQuery: null,
      notesSearch: false
    };
  },
  modals: { DeleteSolutionModal, OutOfRegionSolutionModal, NoteSolutionModal },
  created() {
    this.$store.commit("brain/ui/showControls", false);
  },
  mounted() {
    this.getSolutions();
    EventBus.$on("solutionsSaved", () => {
      this.getSolutions();
    });
  },
  components: {
    deleteSvg,
    arrowSvg,
    downSvg,
    upSvg,
    editSvg,
    crossSvg
  },
  computed: {
    ...mapState("brain", ["region"]),
    sortedSolutions() {
      if (!this.solutions) return [];

      let data = this.solutions;

      let filterKey = this.searchQuery && this.searchQuery.toLowerCase();
      if (filterKey) {
        data = data.filter(function(row) {
          return Object.keys(row).some(function(key) {
            return (
              String(row[key])
                .toLowerCase()
                .indexOf(filterKey) > -1
            );
          });
        });
      }

      if (!this.sorting) return data;

      const ascending = cloneDeep(data).sort((a, b) => {
        return a[this.sorting.property] > b[this.sorting.property] ? 1 : -1;
      });

      return this.sorting.order === "asc" ? ascending : ascending.reverse();
    }
  },
  methods: {
    getSolutions() {
      http
        .get("/api/v2/users/profile/settings/solutions/all")
        .then(response => {
          const solutions = response.data.data.map(solution => {
            solution.solution = JSON.parse(solution.solution);
            return solution;
          });

          this.solutions = this.sortSolutionsByRegion(solutions);
          this.loading = false;
        });
    },
    changeSort(property) {
      if (!this.sorting || this.sorting.property !== property) {
        this.sorting = {
          property: property,
          order: "desc"
        };
      } else {
        this.sorting.order = this.sorting.order === "desc" ? "asc" : "desc";
      }
    },
    sortClass(property) {
      if (this.sorting && this.sorting.property === property) {
        return this.sorting.order;
      } else {
        return null;
      }
    },
    openSolution(row) {
      if (row.solution.region === this.region) {
        console.log("opening this solution:", row);
        this.$store
          .dispatch("brain/solutions/setSolution", row.solution)
          .then(() => {
            this.$router.push({
              name: "advanced",
              params: { fetchFiltersOnInit: false }
            });
            EventBus.$emit("recalculate", this.$router.currentRoute.name);
          });
      } else {
        this.onOutOfRegionSolutionClicked(row);
      }
    },
    onDeleteClicked(row) {
      this.$modalRouter.push({
        name: "DeleteSolutionModal",
        props: { show: true, solution: row },
        model: "show",
        on: { onDeleteSolution: this.deleteSolution }
      });
    },
    onNoteClicked(row) {
      this.$modalRouter.push({
        name: "NoteSolutionModal",
        props: { show: true, initialSolution: row },
        model: "show",
        on: {
          onSaveSolution: this.onSaveSolution
        }
      });
    },
    onOutOfRegionSolutionClicked(row) {
      this.$modalRouter.push({
        name: "OutOfRegionSolutionModal",
        props: { show: true, solution: row },
        model: "show"
      });
    },
    deleteSolution(solution) {
      http({
        method: "delete",
        url: `/api/v2/users/profile/settings/solutions/remove?name=${solution.name}`
      }).then(() => {
        this.getSolutions();
      });
    },
    onSaveSolution(solution) {
      http({
        method: "PUT",
        url: "/api/v2/users/profile/settings/solutions/save",
        data: {
          name: solution.name,
          solution: solution.solution,
          region: solution.region,
          notes: solution.notes
        }
      }).then(() => {
        this.getSolutions();
      });
    },
    sortSolutionsByRegion(solutions) {
      const regionOrderedSolutions = solutions
        .filter(solution => {
          return solution.solution.region === this.region;
        })
        .sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        });

      solutions = solutions
        .filter(solution => {
          return solution.solution.region !== this.region;
        })
        .sort((a, b) => {
          if (a.solution.region > b.solution.region) return 1;
          if (a.solution.region < b.solution.region) return -1;

          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
        });

      solutions.map(solution => {
        regionOrderedSolutions.push(solution);
      });

      return regionOrderedSolutions;
    },

    handleSolutionUpload() {
      if (this.$refs.solutionFile.files.length) {
        this.$store
          .dispatch("brain/solutions/upload", this.$refs.solutionFile.files[0])
          .then(() => {
            this.$router.push({
              name: "advanced",
              params: { fetchFiltersOnInit: false }
            });
            EventBus.$emit("recalculate", this.$router.currentRoute.name);
          });
        this.$refs.solutionFile.value = "";
      }
    },

    backClicked() {
      this.$store.commit("brain/global/setCheckReload", true);
      this.$router.push({ name: "advanced" });
    },

    cleanNotesSearch() {
      this.notesSearch = !this.notesSearch;
      this.searchQuery = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.page-title {
  margin-bottom: 40px;
}

.search-notes {
  position: relative;
  margin-bottom: 20px;

  .cross-icon {
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 15px;
    z-index: 99;
    width: 14px;
  }
}

.back-to-playground {
  margin-top: 115px;
  margin-bottom: 5px;
  display: block;
  color: $blue-500;
  cursor: pointer;
}

.saved-solutions {
  flex-grow: 2;
}

.simple-table {
  margin-bottom: 20px;
}

thead {
  border-top: 1px $mid-gray-200 solid;
  background: #fff;

  th {
    padding: 16px 10px;
  }
}

tbody {
  background: #fff;
}

th {
  cursor: pointer;
  min-width: 100px;
}

td .link-active {
  color: blue;
  display: inline-block;
}

td .link {
  margin-left: 6px;
}

tr {
  cursor: pointer;

  &.disabled {
    cursor: default;
    opacity: 0.5;
  }
}

.delete-icon,
.edit-icon {
  width: 20px;
  path {
    fill: $dark-gray-900;
  }
}

.arrow-icon {
  width: 16px;
  transform: rotate(180deg);

  path {
    fill: $blue-500;
  }
}

.btn {
  cursor: pointer;
}

.up-icon,
.down-icon {
  width: 16px;
}

.meta-info {
  // text-align: center;
  margin: 20px 0;
  color: #676767;
}
</style>
